import type { Referral } from '../../api/types';

/** Every column the table can be ordered by. */
export type SortKey =
  | 'referringEmployeeName'
  | 'newHireName'
  | 'jobTitle'
  | 'department'
  | 'location'
  | 'hireDate'
  | 'probationClearedOn'
  | 'payoutDate'
  | 'payoutAmount'
  | 'referralCount'
  | 'eligibleForPayout'
  | 'payoutAnnouncedToPayroll'
  | 'isPaid'
  | 'completed';

/** What the table is ordered by, or null for the urgency order the list loads in. */
export type Sort = { key: SortKey; descending: boolean } | null;

/**
 * The next state of a header that was just clicked.
 *
 * Three states, not two: ascending, descending, then back to the default. Without the third there is
 * no way back to the urgency order once a column has been sorted — and that order is the one the page
 * is for, so stranding somebody away from it would be the worse half of a good feature.
 */
export function nextSort(current: Sort, key: SortKey): Sort {
  if (current === null || current.key !== key) return { key, descending: false };
  if (!current.descending) return { key, descending: true };

  return null;
}

/**
 * Compares two referrals by one column.
 *
 * Nulls sort last whichever way the column is pointing, because "no department" is not a value that
 * belongs at either end of a list of departments — it is the absence of one, and burying it keeps the
 * rows that have something to say together.
 */
export function compareBy(a: Referral, b: Referral, key: SortKey): number {
  switch (key) {
    // Numbers subtracted rather than compared as text: as strings "900" sorts above "1200".
    case 'payoutAmount':
      return a.payoutAmount - b.payoutAmount;

    case 'referralCount':
      return a.referralCount - b.referralCount;

    // Three states, so an order rather than a flag: turned down, undecided, accepted. Undecided in
    // the middle is what makes sorting this column useful — it gathers the rows waiting on somebody.
    case 'eligibleForPayout':
      return eligibilityRank(a.eligibleForPayout) - eligibilityRank(b.eligibleForPayout);

    case 'payoutAnnouncedToPayroll':
      return Number(a.payoutAnnouncedToPayroll) - Number(b.payoutAnnouncedToPayroll);

    case 'isPaid':
      return Number(a.isPaid) - Number(b.isPaid);

    case 'completed':
      return Number(a.completed) - Number(b.completed);

    // Everything else is a string or an ISO date, and ISO dates sort correctly as text — which is the
    // whole reason this section keeps them as strings rather than as Date.
    default:
      return compareText(a[key], b[key]);
  }
}

function eligibilityRank(eligible: boolean | null): number {
  if (eligible === false) return 0;

  return eligible === null ? 1 : 2;
}

function compareText(a: string | null, b: string | null): number {
  if (a === b) return 0;
  if (a === null || a === '') return 1;
  if (b === null || b === '') return -1;

  return a.localeCompare(b);
}
